__webpack_public_path__ =
  process.env.ENV === 'development'
    ? process.env.WEBPACK_PUBLIC_PATH
    : `${window.$mediavine.web.model.wrapperSrc}/`

import * as loadjs from 'loadjs'
import { AuctionHouse } from './auctionHouse/AuctionHouse'
import Blacklist from './blacklist/Blacklist'
import Chicory from './chicory/Chicory'
import CCPA from './ccpa/ccpa'
import CMP from './cmp/cmp'
import ComScore from './comScore/ComScore'
import { LoadJsBundle } from './enums/LoadJsBundle'
import GoogletagFacade from './googletagFacade/GoogletagFacade'
import GumGum from './partners/gumgum/GumGum'
import { WindowSize } from './helpers'
import { domReady, writeCss } from './helpers/DOM'
import { handleGdprTest } from './helpers/handleGdprTest'
import Logger from './helpers/Logger'
import LiftTest from './liftTest/LiftTest'
import MetricsTracker from './metrics/MetricsTracker'
import WebModel, {
  optimizingForDesktopPagespeed,
  optimizingForMobilePagespeed
} from './models/WebModel'
import PageSettings from './pageSettings/PageSettings'
import PrebidFacade from './prebidFacade/PrebidFacade'
import Refresh from './refresh/Refresh'
import Adhesions from './renderers/Adhesions'
import ATF from './renderers/Atf'
import { ContentHints } from './renderers/ContentHints'
import DynamicContentSlots from './renderers/DynamicContentSlots'
import { GenericHints } from './renderers/GenericHints'
import Lazy from './renderers/Lazy'
import { ListHints } from './renderers/ListHints'
import InjectableCreative from './injectableCreative/InjectableCreative'
import SlotFactory from './slots/SlotFactory'
import { StickySlot } from './stickySlot/StickySlot'
import StickyVideo from './stickyVideo/StickyVideo'
import { getBaseStyles } from './styles/base'
import Zergnet from './zergnet/Zergnet'
import { IndexExchange } from './partners/indexExchange/IndexExchange'
import { AdReporter } from './adReporter/AdReporter'
import AdTags from './prebidFacade/videoBids/getAdTags'
import { doOnce } from './helpers/doOnce'
import Keywords from './keywords/Keywords'
import { Usersync } from './usersync/Usersync'
import { polyfill } from './helpers/polyfills'
import PageIdLookup from './pageIdLookup/PageIdLookup'
import { USP } from './ccpa/USP'

const debug = (msg: string) => Logger.custom(['debug', 'wrapper'], msg)
let model: WebModel
let ccpa: CCPA
let cmp: CMP

polyfill()

domReady(() => {
  USP.init(window.$mediavine.web.model)
  StickyVideo.setVideoTargetDivs(window.$mediavine.web.model)
  window.$mediavine.web.videoOptionsSet = true
})

export enum ExecutionStatus {
  halted,
  delayed,
  executed
}

export const init = async (): Promise<ExecutionStatus> => {
  if (window.$mediavine.web.initialized) {
    debug('Wrapper previously initialized. Halting Execution.')

    return ExecutionStatus.halted
  }

  window.$mediavine.web.initialized = true
  window.$mediavine.web.load = doOnce(() => load(model))

  new AdReporter()
  await handleGdprTest(window.$mediavine.web.model)

  model = new WebModel(window.$mediavine.web.model)
  ccpa = new CCPA(model)
  cmp = new CMP(model)

  ccpa.initModal()

  if (optimizingForMobilePagespeed() || optimizingForDesktopPagespeed()) {
    debug('Pagespeed Optimization. Delaying execution until scroll.')

    const callback = () => {
      debug('Scroll detected. Executing.')
      window.removeEventListener('scroll', callback, false)
      window.removeEventListener('beforeprint', callback, false)

      load(model)
    }

    window.addEventListener('scroll', callback, false)
    window.addEventListener('beforeprint', callback, false)

    return ExecutionStatus.delayed
  } else {
    load(model)

    return ExecutionStatus.executed
  }
}

export const load = async (model: WebModel): Promise<ExecutionStatus> => {
  /** Render Validations */
  if (
    domainIsNotValid() ||
    siteIsDeactivated(model) ||
    siteIsKillswitched(model) ||
    onPrivacyPolicyPage(model) ||
    windowIsTooSmall()
  ) {
    return ExecutionStatus.halted
  }

  writeCss(getBaseStyles(model))
  const keywordService = new Keywords()
  const pageSettings = new PageSettings()
  const blacklist = new Blacklist(model, pageSettings)
  const auctionHouse = new AuctionHouse(model)
  const pageIdLookup = new PageIdLookup(model)
  await PrebidFacade.loadPrebidImports()
  const googletagFacade = new GoogletagFacade(
    model,
    auctionHouse,
    keywordService
  )
  const liftTest = new LiftTest(model, googletagFacade)
  const adTags = new AdTags(
    window.$mediavine.web.model,
    keywordService,
    liftTest
  )

  window.$mediavine.web.getVideoAdTag = adTags.getAdTag.bind(adTags)
  window.$mediavine.web.getNonLinearVideoAdTag = adTags.getNonLinearAdTag.bind(
    adTags
  )

  if (blacklist.all) {
    debug('Blacklist.all triggered. Halting execution.')
    // Footer branding is omitted from global blacklisting
    new ComScore(model)
    return ExecutionStatus.halted
  }

  // Gotta wait for GDPR stuff to be accepted before continuing.
  loadjs.ready(LoadJsBundle.gdpr, () => {
    Usersync.init(model)
    IndexExchange.init(model, blacklist)
    const slotFactory = new SlotFactory(model, blacklist, auctionHouse)
    const prebidFacade = new PrebidFacade(model, cmp, auctionHouse)

    // Feature Modules
    new MetricsTracker(model)
    new InjectableCreative(model)
    new StickySlot(model)
    const refresh = new Refresh(prebidFacade)
    new Chicory(model, blacklist)

    // Ad Renderers
    const reRenderables: Array<{ render: Function }> = []
    reRenderables.push(
      new DynamicContentSlots(model, prebidFacade, slotFactory)
    )

    reRenderables.push(new StickyVideo(model, blacklist))
    new Adhesions(model, slotFactory, prebidFacade, blacklist)
    new ATF(model, slotFactory, prebidFacade)
    reRenderables.push(new Lazy(model, slotFactory, prebidFacade))
    reRenderables.push(new ListHints(prebidFacade, slotFactory))
    reRenderables.push(new ContentHints(prebidFacade, slotFactory))
    reRenderables.push(new GenericHints(prebidFacade, slotFactory))

    setInterval(() => reRenderables.forEach((r) => r.render()), 1000)

    // third-party partner modules
    new ComScore(model)
    new GumGum(model, blacklist, refresh)
    new Zergnet(model, blacklist)
  })

  return ExecutionStatus.executed
}

window.$mediavine.web.init = init

function domainIsNotValid(): boolean {
  const isNotValid =
    window.$mediavine.web.model.whitelistDomain &&
    !!window.$mediavine.web.model.domain &&
    window.$mediavine.web.model.domain !== window.location.hostname

  if (isNotValid) {
    debug('Domain not whitelisted. Halting Execution.')
  }

  return isNotValid
}

const validate = (bool: boolean, errMessage: string): boolean => {
  if (bool) {
    debug(`${errMessage}. Halting execution.`)
  }
  return bool
}

const siteIsDeactivated = (model): boolean =>
  validate(model.deactivated, 'Site has been deactivated.')
const siteIsKillswitched = (model): boolean =>
  validate(model.killswitch, 'Site killswitch is activated.')
const windowIsTooSmall = (): boolean =>
  validate(WindowSize.isWindowTooSmall(), 'Window is too small.')
const onPrivacyPolicyPage = (model): boolean =>
  validate(
    model.privacy_policy_link ===
      `${window.location.origin}${window.location.pathname}`,
    "On site's privacy page."
  )
